import { defineComponent as _defineComponent } from 'vue'
import CcCheckbox from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckbox.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcSteps from '@/views/Automation/CampaignCreatorV2/components/CcSteps/CcSteps.ts.vue';
import CcModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcModal.ts.vue';
import CreateCampaign from '@/views/Automation/CampaignCreatorV2/CreateCampaign.ts.vue';
import { useTourReset } from '@/views/Automation/CampaignCreatorV2/helpers/tourGuide';
import ManageCampaign from '@/views/Automation/CampaignCreatorV2/ManageCampaign.ts.vue';
import PreviewPublish from '@/views/Automation/CampaignCreatorV2/PreviewPublish.ts.vue';
import useAdStore from '@/views/Automation/CampaignCreatorV2/store/Ad';
import useAdSetStore from '@/views/Automation/CampaignCreatorV2/store/AdSet';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import useNavigationStore from '@/views/Automation/CampaignCreatorV2/store/Navigation';
import { Action, Level, MainStep, NavigationMenu } from '@/views/Automation/CampaignCreatorV2/types';
import { PopoverTriggers, Position } from '@sh/types';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onUnmounted, ref } from 'vue';

interface Props {
  visibility: boolean;
}

interface Emits {
  (event: 'onVisibilityChange', value: boolean): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Index.ts',
  props: {
    visibility: { type: Boolean, default: false }
  },
  emits: ["onVisibilityChange"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'onVisibilityChange', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

/* eslint-disable import/no-cycle */




const campaignStore = useCampaignStore();
const campaignGetters = storeToRefs(campaignStore);
const adsetStore = useAdSetStore();
const adStore = useAdStore();
const navigationStore = useNavigationStore();

const activeStep = ref(MainStep.CREATE_CAMPAIGN);
const steps = ref(Object.values(MainStep));
const isFinishModalOpen = ref(false);
const isCreatingCampaignsModalOpen = ref(false);
const template = ref({
  name: '',
  active: false,
});

const finalCheckLoading = ref(false);
const generatedTimeText = ref({
  text: '',
  error: '',
});
const lastUpdated = ref(0);
const saveTemplateNameState = ref({
  inputValue: '',
  finalValue: 'Untitled Template Campaign',
  lastUpdatedIntervalId: 0,
  disabledInput: true,
  actions: [
    {
      label: 'Save',
      event: 'onSaveNameClick',
    },
    {
      label: 'Save As',
      event: 'onSaveNameAsClick',
    },
  ] as Action[],
  isButtonLoading: false,
  showMoreSaveActions: false,
});
const saveAsConfirmationModal = ref(false);

const currentTemplateInfo = computed(() => {
  return campaignGetters.getCurrentTemplateInfo.value;
});

const saveNameInputRef = ref<NullableType<HTMLInputElement>>(null);

const activeStepPosition = computed(() => steps.value.indexOf(activeStep.value));

const hasErrors = computed(() => {
  const campaigns = campaignStore.getItemsForNavigation;
  const adsets = adsetStore.getItemsForNavigation;
  const ads = adStore.getItemsForNavigation;

  return (
    campaigns.some((campaign) => !!campaign.message) ||
    adsets.some((adset) => !!adset.message) ||
    ads.some((ad) => !!ad.message)
  );
});

function getNextEntity(entity: Level) {
  switch (entity) {
    case Level.Campaign:
      return Level.AdSet;
    case Level.AdSet:
      return Level.Ad;
    case Level.Ad:
      throw new Error('There is no next level after Ad');
  }
}

function navigateToFirstError(entity: Level, items: NavigationMenu[]) {
  for (const item of items) {
    if (item.message) {
      navigationStore.onActiveSelect(
        entity,
        entity === Level.Campaign ? item.id : item.campaignId ?? item.id,
        entity === Level.AdSet ? item.id : item.adSetId,
        entity === Level.Ad ? item.id : undefined
      );
      return true;
    }
    if (item.children?.length && navigateToFirstError(getNextEntity(entity), item.children)) {
      return true;
    }
  }
}

function onStepChange(position: number, isForwards = true) {
  const step = steps.value[position];
  const newPosition = isForwards ? position + 1 : position - 1;
  const newStep = steps.value[newPosition];

  switch (step) {
    case MainStep.CREATE_CAMPAIGN:
      campaignStore.validateForm();
      if (hasErrors.value) {
        navigateToFirstError(Level.Campaign, navigationStore.getNavigation);
        navigationStore.setNextStepButtonErrorTooltip(
          'Please check your campaigns. There are items that need completion or correction.'
        );
        navigationStore.triggerTransition();
      } else {
        campaignStore.updateAbGroups(false);
        activeStep.value = newStep;
      }
      break;
    case MainStep.MANAGE_CAMPAIGN:
      campaignStore.updateAbGroups(true);
      navigationStore.setNextStepButtonErrorTooltip('');
      activeStep.value = newStep;
      break;
    case MainStep.PREVIEW_PUBLISH:
      navigationStore.setNextStepButtonErrorTooltip('');
      if (isForwards) {
        isFinishModalOpen.value = true;
      } else {
        campaignStore.updateAbGroups();
        activeStep.value = newStep;
      }
      break;
    default:
      break;
  }
}

function onModalClose() {
  template.value = {
    name: '',
    active: false,
  };
  isFinishModalOpen.value = false;
}

async function onSaveClick() {
  finalCheckLoading.value = true;
  try {
    await campaignStore.saveTasks();
    await campaignStore.saveAsTemplate(template.value.active, template.value.name);
  } catch (error) {
    console.error(error);
  } finally {
    finalCheckLoading.value = false;
    onModalClose();
    isCreatingCampaignsModalOpen.value = true;
  }
}

async function onSaveNameEnable() {
  saveTemplateNameState.value.disabledInput = false;
  await nextTick();
  saveNameInputRef.value?.focus();
}

function onNameActionClick(saveChanges: boolean) {
  if (saveChanges) {
    saveTemplateNameState.value.disabledInput = true;
    campaignStore.updateTemplateName(saveTemplateNameState.value.inputValue);
  } else {
    saveTemplateNameState.value.disabledInput = true;
  }
}

async function onSaveActionClick(event: string) {
  saveTemplateNameState.value.isButtonLoading = true;
  saveTemplateNameState.value.showMoreSaveActions = false;

  try {
    if (event === 'onSaveNameClick') {
      await campaignStore.saveStateTemplate();
    }
    if (event === 'onSaveNameAsClick') {
      saveAsConfirmationModal.value = true;
      saveTemplateNameState.value.isButtonLoading = false;
      return;
    }
    if (event === 'onSaveNameAsConfirmationClick') {
      saveAsConfirmationModal.value = false;
      await campaignStore.saveAsTemplate(true);
    }

    lastUpdated.value = new Date().getTime();
    const generatedText = moment(lastUpdated.value).fromNow();
    generatedTimeText.value.text = `Last saved ${generatedText}`;
    generatedTimeText.value.error = '';
  } catch {
    generatedTimeText.value.error = 'Failed to save changes.';
  }
  onNameActionClick(false);
  saveTemplateNameState.value.isButtonLoading = false;
}

onBeforeMount(() => {
  saveTemplateNameState.value.lastUpdatedIntervalId = setInterval(() => {
    if (campaignGetters.currentTemplate.value.lastSaved && !generatedTimeText.value.error) {
      lastUpdated.value = new Date(campaignGetters.currentTemplate.value.lastSaved).getTime();
      const generatedText = moment(lastUpdated.value).fromNow();
      generatedTimeText.value.text = `Last saved ${generatedText}`;
    }
  }, 10000);
});

onUnmounted(() => {
  clearInterval(saveTemplateNameState.value.lastUpdatedIntervalId);
});

return { __sfc: true,emits, campaignStore, campaignGetters, adsetStore, adStore, navigationStore, activeStep, steps, isFinishModalOpen, isCreatingCampaignsModalOpen, template, finalCheckLoading, generatedTimeText, lastUpdated, saveTemplateNameState, saveAsConfirmationModal, currentTemplateInfo, saveNameInputRef, activeStepPosition, hasErrors, getNextEntity, navigateToFirstError, onStepChange, onModalClose, onSaveClick, onSaveNameEnable, onNameActionClick, onSaveActionClick, CcCheckbox, CcIcon, CcSteps, CcModal, CreateCampaign, useTourReset, ManageCampaign, PreviewPublish, MainStep, PopoverTriggers, Position }
}

})